import React, { useDeferredValue, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputBase,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  AlphaDownIcon,
  AlphaDownAltIcon,
  ArrowRightIcon,
  ClearFilterLeftIcon,
  ClearFilterRightIcon,
  NumberSignIcon,
  TextIcon,
  ColumnSearchIcon,
} from '../CommonIcons/Icons';
import { getDistinctValues, getOperators, inferFilterType, getSubmenuLabel } from './FilterEngine';
import { getSortLabels } from './types';
import type { FilterableColumn, FilterCondition, FilterOperator, SortDirection } from './types';
import type { UseColumnFiltersResult } from './useColumnFilters';
import CustomFilterDialog from './CustomFilterDialog';

const INITIAL_OPTION_LIMIT = 100;
const VALUE_LIST_HEIGHT = 160;
const ALL_OPTION_VALUE = 'all';


export interface ColumnFilterControlProps {
  column: FilterableColumn;
  columns: FilterableColumn[];
  rows?: any[];
  controller: UseColumnFiltersResult;
  isOpen?: boolean;
  getColumnValues?: (field: string) => Promise<any[]>;
  activeSortDirection?: SortDirection;
  onClose?: () => void;
}

const getOptionValue = (option: any): any =>
  option && typeof option === 'object' && Object.hasOwn(option, 'value')
    ? option.value
    : option;

const isBlankOption = (option: any): boolean => {
  if (option === null || option === undefined || option === '') return true;
  const value = getOptionValue(option);
  if (value === null || value === undefined || value === '') return true;
  const str = String(value).trim().toLowerCase();
  return str === '' || str === '(blank)' || str === 'null' || str === 'undefined';
};

const labelFor = (value: any): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

const getOptionLabel = (option: any, column: FilterableColumn): string => {
  if (option && typeof option === 'object' && Object.hasOwn(option, 'label')) {
    return String(option.label);
  }
  const value = getOptionValue(option);
  return column.formatFilterValue?.(value) ?? labelFor(value);
};

const UncheckedFilterIcon = ({ borderColor, backgroundColor }: { borderColor: string; backgroundColor: string }) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '4px',
      border: `1px solid ${borderColor}`,
      backgroundColor,
      boxSizing: 'border-box',
    }}
  />
);

const CheckedFilterIcon = ({ borderColor, checkColor, backgroundColor }: { borderColor: string; checkColor: string; backgroundColor: string }) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '4px',
      border: `1px solid ${borderColor}`,
      backgroundColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
    }}
  >
    <CheckIcon sx={{ fontSize: 14, color: checkColor, fill: checkColor }} />
  </Box>
);

export const ColumnFilterControl: React.FC<ColumnFilterControlProps> = ({
  column,
  columns,
  rows = [],
  controller,
  isOpen = false,
  getColumnValues,
  activeSortDirection,
  onClose,
}) => {
  const theme = useTheme();
  const colors = theme.palette as any;
  const dropdownColors = colors.filterDropdown;

  const {
    filterModel,
    sortModel,
    setColumnFilter,
    clearColumnFilter,
    getIconState,
    isSortLimitReached,
    toggleSort,
    clearAllFilters,
  } = controller;
  const currentFilter = filterModel[column.key] ?? null;
  const hasSortForColumn = sortModel.some((sort) => sort.columnKey === column.key);

  const getCurrentDraftValues = () => {
      const raw = Array.isArray(currentFilter?.selOptions)
        ? currentFilter.selOptions.map(String)
        : currentFilter?.values?.map(String) ?? [];

      if (typeof column.isFilterOptionDisabled !== 'function') return raw;

      // "All" can't exist for exclusive columns (the All row is hidden), so drop stale ones
      if (raw.includes(ALL_OPTION_VALUE)) return [];

      const hasConflict = raw.some((v) => column.isFilterOptionDisabled!(v, raw));
      return hasConflict ? raw.filter((v) => !/^in[\s_-]?progress$/i.test(v)) : raw;
  };

  const [asyncValues, setAsyncValues] = useState<any[] | null>(null);
  const [loadingValues, setLoadingValues] = useState(false);
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [miscellaneousAnchor, setMiscellaneousAnchor] = useState<HTMLElement | null>(null);
  const [customFilterOperator, setCustomFilterOperator] = useState<FilterOperator | null>(null);
  const [customFilterPosition, setCustomFilterPosition] = useState<{ top: number; left: number } | undefined>();
  const [pendingCustomCondition, setPendingCustomCondition] = useState<FilterCondition | null>(null);
  const [pendingColumnFilterClear, setPendingColumnFilterClear] = useState(false);
  const [pendingClearAllFilters, setPendingClearAllFilters] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [draftValues, setDraftValues] = useState<string[]>(getCurrentDraftValues);
  const [replacesCondition, setReplacesCondition] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const submenuCloseTimer = React.useRef<number | null>(null);
  const miscellaneousCloseTimer = React.useRef<number | null>(null);

  const clearSubmenuCloseTimer = () => {
    if (submenuCloseTimer.current !== null) {
      window.clearTimeout(submenuCloseTimer.current);
      submenuCloseTimer.current = null;
    }
  };

  const scheduleSubmenuClose = () => {
    clearSubmenuCloseTimer();
    submenuCloseTimer.current = window.setTimeout(() => {
      setSubmenuAnchor(null);
      submenuCloseTimer.current = null;
    }, 120);
  };

  const clearMiscellaneousCloseTimer = () => {
    if (miscellaneousCloseTimer.current !== null) {
      window.clearTimeout(miscellaneousCloseTimer.current);
      miscellaneousCloseTimer.current = null;
    }
  };

  const scheduleMiscellaneousClose = () => {
    clearMiscellaneousCloseTimer();
    miscellaneousCloseTimer.current = window.setTimeout(() => {
      setMiscellaneousAnchor(null);
      miscellaneousCloseTimer.current = null;
    }, 120);
  };

  const dataType = useMemo(() => inferFilterType(rows, column), [rows, column]);
  const operators = getOperators(dataType).filter(
    (o) => !column.filterOperators || column.filterOperators.includes(o.operator)
  );
  const submenuLabel = getSubmenuLabel(dataType);
  const columnLabel = typeof column.name === 'string' ? column.name : String(column.key);

  const closeCustomFilter = () => {
    setSubmenuAnchor(null);
    setCustomFilterOperator(null);
    setCustomFilterPosition(undefined);
  };

  const handleApplyCustomFilter = (condition: FilterCondition) => {
    setDraftDirty(true);
    setPendingColumnFilterClear(false);
    setPendingClearAllFilters(false);
    const selection = hasValueList ? column.getCustomFilterSelection?.(condition, selectableValues) ?? null : null;
    if (selection) setDraftValues(selection);
    setPendingCustomCondition(selection ? null : condition);
    setReplacesCondition(Boolean(selection));
    closeCustomFilter();
  };

  const resetDraftFilter = () => {
    setDraftValues(getCurrentDraftValues());
    setPendingCustomCondition(null);
    setReplacesCondition(false);
    setPendingColumnFilterClear(false);
    setPendingClearAllFilters(false);
    setDraftDirty(false);
  };

  const previousOpen = React.useRef(false);
  React.useEffect(() => {
    if (isOpen && !previousOpen.current) resetDraftFilter();
    previousOpen.current = isOpen;
  }, [isOpen]);

  const hasAppliedColumnFilter = Boolean(
    currentFilter?.values?.length ||
    currentFilter?.selOptions?.length ||
    currentFilter?.condition ||
    hasSortForColumn
  );
  const hasAnyAppliedState =
    Object.keys(filterModel).length > 0 || sortModel.length > 0 || pendingClearAllFilters;

  const sortLabels = getSortLabels(column.key, column.columnDetails);
  const iconState = getIconState(column.key);
  const ascActive = (activeSortDirection ?? iconState.sortDirection) === 'asc';
  const descActive = (activeSortDirection ?? iconState.sortDirection) === 'desc';
  const sortDisabled = isSortLimitReached && !iconState.sortDirection;
  const isDateColumn = dataType === 'date';
  const hasValueList = !isDateColumn && column.showValueList !== false;
  const hasOperators = !isDateColumn && operators.length > 0;

  const SubmenuIconComponent = dataType === 'number' ? NumberSignIcon : TextIcon;

  React.useEffect(() => {
    if (!getColumnValues || hasLoaded || dataType === 'date') return;
    setHasLoaded(true);
    setLoadingValues(true);
    getColumnValues(column.filterField ?? column.key)
      .then(setAsyncValues)
      .finally(() => {
        setLoadingValues(false);
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      });
  }, [getColumnValues, hasLoaded, dataType, column.filterField, column.key]);

  const values = useMemo(() => {
    if (getColumnValues) return asyncValues ?? [];
    return getDistinctValues(rows, column, filterModel, columns);
  }, [getColumnValues, asyncValues, rows, column, filterModel, columns]);

  const miscellaneousOption = useMemo(
    () => values.find((value: any) => value && typeof value === 'object' && Array.isArray(value.children)),
    [values]
  );
  const miscellaneousValues = useMemo(
    () => (miscellaneousOption?.children ?? []).filter((value: any) => !isBlankOption(value)),
    [miscellaneousOption]
  );
  const regularValues = useMemo(
    () => values.filter((value: any) => value !== miscellaneousOption && !isBlankOption(value)),
    [values, miscellaneousOption]
  );
  const selectableValues = useMemo(
    () => [...regularValues, ...miscellaneousValues],
    [regularValues, miscellaneousValues]
  );
  const selectableValueKeys = useMemo(
    () => selectableValues.map((value) => String(getOptionValue(value))),
    [selectableValues]
  );
  const selectedValueSet = useMemo(() => new Set(draftValues), [draftValues]);
  const allValuesSelected = selectedValueSet.has(ALL_OPTION_VALUE) || (
    selectableValueKeys.length > 0 && selectableValueKeys.every((value) => selectedValueSet.has(value))
  );
  const getExpandedDraftValues = () => selectedValueSet.has(ALL_OPTION_VALUE)
    ? selectableValueKeys
    : draftValues.filter((value) => value !== ALL_OPTION_VALUE);
    
  const hasExclusiveRule = typeof column.isFilterOptionDisabled === 'function';

const isOptionDisabled = (value: string) =>
  hasExclusiveRule ? column.isFilterOptionDisabled!(value, getExpandedDraftValues()) : false;

const updateDraftSelection = (nextValues: Set<string>) => {
  const allSelected =
    !hasExclusiveRule &&
    selectableValueKeys.length > 0 &&
    selectableValueKeys.every((value) => nextValues.has(value));
  setDraftValues(allSelected ? [ALL_OPTION_VALUE] : Array.from(nextValues));
};

  const [valueSearch, setValueSearch] = useState('');
  const deferredValueSearch = valueSearch.trim().toLowerCase();
  const buildUniqueValueOptions = (values: any[], column: FilterableColumn) => {
    const seen = new Set<string>();
    return values
      .filter((option: any) => !isBlankOption(option))
      .map((option: any) => {
        const label = getOptionLabel(option, column);

        return {
          option,
          label,
          normalizedLabel: label.trim().toLowerCase(),
          value: String(getOptionValue(option)),
        };
      })
      .filter((item) => {
        if (!item.label || item.normalizedLabel === '(blank)' || item.normalizedLabel === '') {
          return false;
        }
        if (seen.has(item.normalizedLabel)) {
          return false;
        }

        seen.add(item.normalizedLabel);
        return true;
      });
  };
  const regularValueOptions = useMemo(
  () => buildUniqueValueOptions(regularValues, column),
  [regularValues, column]
);
  // const regularValueOptions = useMemo(() => regularValues.map((option: any) => {
  //   const label = getOptionLabel(option, column);
  //   return {
  //     label,
  //     normalizedLabel: label.toLowerCase(),
  //     value: String(getOptionValue(option)),
  //   };
  // }), [regularValues, column]);
  const miscellaneousValueOptions = useMemo<Array<{
    option: any;
    label: string;
    normalizedLabel: string;
    value: string;
  }>>(() => miscellaneousValues.map((option: any) => {
    const label = getOptionLabel(option, column);
    return {
      option,
      label,
      normalizedLabel: label.toLowerCase(),
      value: String(getOptionValue(option)),
    };
  }), [miscellaneousValues, column]);
  const searchableMiscellaneousOptions = miscellaneousValueOptions.slice(0, INITIAL_OPTION_LIMIT);
  const visibleMiscellaneousValues = useMemo(() => {
    if (!deferredValueSearch || 'miscellaneous'.includes(deferredValueSearch)) {
      return searchableMiscellaneousOptions;
    }
    return miscellaneousValueOptions
      .filter((option: { normalizedLabel: string }) =>
        option.normalizedLabel.includes(deferredValueSearch)
      )
      .slice(0, INITIAL_OPTION_LIMIT);
  }, [deferredValueSearch, searchableMiscellaneousOptions]);

  const showMiscellaneousOption = Boolean(
    miscellaneousOption && miscellaneousValues.length > 0 &&
    (!deferredValueSearch || 'miscellaneous'.includes(deferredValueSearch) || visibleMiscellaneousValues.length > 0)
  );

  const filteredValueOptions = useMemo(() => {
    if (deferredValueSearch) {
      return regularValueOptions
        .filter((option) => option.normalizedLabel.includes(deferredValueSearch))
        .slice(0, INITIAL_OPTION_LIMIT);
    }

    const initialOptions = regularValueOptions.slice(0, INITIAL_OPTION_LIMIT);
    const initialValueSet = new Set(initialOptions.map((option) => option.value));
    const selectedOptionsOutsideInitialList = regularValueOptions.filter(
      (option) => selectedValueSet.has(option.value) && !initialValueSet.has(option.value)
    );
    const options = [...initialOptions, ...selectedOptionsOutsideInitialList];
    // Values ticked from a Custom Filter (getCustomFilterSelection) are listed first so they are visible
    return replacesCondition
      ? [...options.filter((option) => selectedValueSet.has(option.value)), ...options.filter((option) => !selectedValueSet.has(option.value))]
      : options;
  }, [deferredValueSearch, regularValueOptions, selectedValueSet, replacesCondition]);

  const hasFilteredValueOptions = filteredValueOptions.length > 0 || showMiscellaneousOption;
  const allMiscellaneousSelected = miscellaneousValues.length > 0 && miscellaneousValues.every(
    (value: any) => selectedValueSet.has(ALL_OPTION_VALUE) || selectedValueSet.has(String(getOptionValue(value)))
  );
  const someMiscellaneousSelected = miscellaneousValues.some(
    (value: any) => selectedValueSet.has(ALL_OPTION_VALUE) || selectedValueSet.has(String(getOptionValue(value)))
  );

  const toggleMiscellaneousValues = () => {
    const nextValues = new Set(getExpandedDraftValues());
    if (allMiscellaneousSelected) {
      miscellaneousValues.forEach((value: any) => nextValues.delete(String(getOptionValue(value))));
    } else {
      miscellaneousValues.forEach((value: any) => nextValues.add(String(getOptionValue(value))));
    }
    updateDraftSelection(nextValues);
    setPendingClearAllFilters(false);
    setDraftDirty(true);
  };

  const toggleMiscellaneousValue = (value: any) => {
    const key = String(getOptionValue(value));
    const nextValues = new Set(getExpandedDraftValues());
    if (nextValues.has(key)) nextValues.delete(key);
    else nextValues.add(key);
    updateDraftSelection(nextValues);
    setPendingClearAllFilters(false);
    setDraftDirty(true);
  };

  const renderSortMenuItems = () => {
    const sortRow = (
      direction: 'asc' | 'desc',
      label: string,
      active: boolean,
      ActiveIcon: React.ComponentType<{ color?: string }>,
      IdleIcon: React.ComponentType<{ color?: string }>
    ) => (
      <MenuItem
        className="filter-row"
        onClick={() => {
          if (sortDisabled) return;
          toggleSort(column.key, direction);
          onClose?.();
        }}
        disabled={sortDisabled}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {active ? <ActiveIcon color={colors.inputNew.inputText} /> : <IdleIcon color={colors.inputNew.inputText} />}
            <Typography
              variant="bodyText"
              sx={{ fontSize: '14px', fontWeight: 400 }}
            >
              {label}
            </Typography>
          </Stack>
          {(direction === 'asc' ? ascActive : descActive) && (
            <CheckIcon sx={{ fontSize: '16px', color: '#003771' }} />
          )}
        </Stack>
      </MenuItem>
    );

    // NOTE: no trailing Divider here — Sort and Clear are one continuous
    // group in the Figma with no rule between them.
    return (
      <>
        {sortRow(sortLabels.ascending.direction, sortLabels.ascending.label, ascActive, AlphaDownIcon, AlphaDownIcon)}
        {sortRow(sortLabels.descending.direction, sortLabels.descending.label, descActive, AlphaDownAltIcon, AlphaDownAltIcon)}
        {sortDisabled && (
          <Typography sx={{ px: 2, py: 0.5, fontSize: 11, color: '#B25E00' }}>
            Three columns are already sorted. Clear one to sort by this column.
          </Typography>
        )}
      </>
    );
  };

  const renderClearMenuItems = () => (
    <div>
      <MenuItem
        className="filter-row"
        onClick={() => {
          setDraftValues([]);
          setPendingCustomCondition(null);
          setPendingColumnFilterClear(true);
          setPendingClearAllFilters(false);
          setDraftDirty(true);
        }}
        disabled={!hasAppliedColumnFilter || pendingColumnFilterClear || pendingClearAllFilters}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <ClearFilterLeftIcon color={colors.inputNew.inputText} />
          <Typography variant="bodyText">Clear Column Filter</Typography>
        </Stack>
      </MenuItem>
      <MenuItem
        className="filter-row"
        onClick={() => {
          setPendingClearAllFilters(true);
          setPendingColumnFilterClear(false);
          setPendingCustomCondition(null);
          setDraftDirty(true);
        }}
        disabled={!hasAnyAppliedState || pendingClearAllFilters}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <ClearFilterRightIcon color={colors.inputNew.inputText} />
          <Typography variant="bodyText">Clear All Filters</Typography>
        </Stack>
      </MenuItem>
    </div>
  );

  const renderFooterButtons = () => (
    <div
      className="buttonSec"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        width: '100%',
      }}
    >
      <Button
        variant="text"
        disableRipple
        onClick={() => {
          resetDraftFilter();
          onClose?.();
        }}
        sx={{
          flex: 1,
          textTransform: 'uppercase',
          fontSize: '16px',
          fontWeight: 600,
          color: '#39ACE7 !important',
          minWidth: 0,
          px: 0.5,
          py: 0,
          borderRadius: '4px 0 0 4px',
          '&:hover': { background: 'transparent' },
        }}
      >
        CLOSE
      </Button>
      <Button
        variant="contained"
        disableElevation
        onClick={() => {
          const appliedValues = dataType === 'number' && !draftValues.includes(ALL_OPTION_VALUE)
            ? draftValues.map((value) => Number(value))
            : draftValues;
          if (!draftDirty) {
            onClose?.();
            return;
          }
          if (pendingClearAllFilters) {
            clearAllFilters();
          } else if (pendingColumnFilterClear) {
            clearColumnFilter(column.key);
          } else {
            setColumnFilter(column.key, {
              values: appliedValues,
              condition: pendingCustomCondition ?? (replacesCondition ? null : currentFilter?.condition) ?? null,
              selOptions: draftValues,
            });
          }
          setDraftValues(appliedValues.map(String));
          setPendingCustomCondition(null);
          setReplacesCondition(false);
          setPendingColumnFilterClear(false);
          setPendingClearAllFilters(false);
          setDraftDirty(false);
          onClose?.();
        }}
        sx={{
          flex: 1,
          textTransform: 'uppercase',
          fontSize: '16px',
          fontWeight: 600,
          color: `${colors.inputNew.saveText} !important`,
          background: '#39ACE7 !important',
          borderRadius: '0 4px 4px 0 !important',
          boxShadow: 'none',
          minWidth: 0,
          px: 2.25,
          py: 0.5,
          '&:hover': {
            background: '#2E9BD6 !important',
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            color: `${colors.inputNew.saveText} !important`,
            background: '#69c5f3ff !important',
          },
        }}
      >
        OK
      </Button>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {renderSortMenuItems()}
      <Divider
        sx={{
          my: 0.5,
          borderColor: `${colors.inputNew.inputPlaceholder}`,
        }}
      />
      {renderClearMenuItems()}

      {(hasOperators || dataType === 'boolean') && (
        <Divider
          sx={{
            my: 0.5,
            borderColor: `${colors.inputNew.inputPlaceholder}`,
          }}
        />
      )}

      {hasOperators && (
        <MenuList dense disablePadding sx={{ width: '100%', position: 'relative', zIndex: 2 }}>
          <MenuItem
            onMouseEnter={(e) => {
              clearSubmenuCloseTimer();
              setSubmenuAnchor(e.currentTarget);
            }}
            onMouseLeave={() => scheduleSubmenuClose()}
            onClick={(e) => {
              clearSubmenuCloseTimer();
              setSubmenuAnchor(e.currentTarget);
            }}
            sx={{
              width: '100%',
              minHeight: 32,
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 1.5,
              fontSize: 13,
              borderRadius: 0,
              padding: '12px !important',
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                width: 16,
                height: 16,
                flexShrink: 0,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                lineHeight: 0,
                color: '#4A5A6A',
                '& > *': {
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                },
              }}
            >
              <SubmenuIconComponent color={colors.inputNew.inputText} />
            </Box>
            <Typography variant="bodyText" component="span" sx={{ flex: 1, minWidth: 0 }}>
              {submenuLabel}
            </Typography>
            <ArrowRightIcon color={colors.dateFilter.presetArrow} />
          </MenuItem>
        </MenuList>
      )}

      {hasOperators && hasValueList && (
        <Divider
          sx={{
            my: 0.5,
            borderColor: `${colors.inputNew.inputPlaceholder}`,
          }}
        />
      )}

      {hasValueList && (
        <Box sx={{ pt: 1, pb: 0.5, width: '100%' }}>
          <Box sx={{ width: '100%', background: colors.inputNew.saveText, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                paddingBottom: 0.75,
                borderBottom: '1px solid #F3F5F8',
              }}
            >
              <ColumnSearchIcon color={dropdownColors.searchIcon} />
              <InputBase
                value={valueSearch}
                onChange={(event) => setValueSearch(event.target.value)}
                placeholder={`Search ${columnLabel}`}
                endAdornment={valueSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      onClick={() => setValueSearch('')}
                      edge="end"
                      size="small"
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                )}
                sx={{
                  width: '100%',
                  fontSize: 13,
                  color: dropdownColors.searchText,
                  '& input': {
                    color: colors.inputNew.inputText,
                  },
                  '& input::placeholder': {
                    color: dropdownColors.searchPlaceholder,
                    opacity: 1,
                  },
                }}
              />
            </Box>

            <MenuList
              dense
              disablePadding
              sx={{
                maxHeight: VALUE_LIST_HEIGHT,
                overflowY: 'auto',
                width: '100%',
                background: `${dropdownColors.panelBackground} !important`,
                scrollbarWidth: 'thin',
                scrollbarColor: `${dropdownColors.checkboxCheck} ${dropdownColors.panelBorder}`,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: dropdownColors.panelBorder,
                },
                '&::-webkit-scrollbar-thumb': {
                  background: dropdownColors.checkboxCheck,
                  borderRadius: '8px',
                  border: `2px solid ${dropdownColors.panelBorder}`,
                },
              }}
            >
              {loadingValues && (
                <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>Loading options...</Typography>
                </Box>
              )}

              {!loadingValues &&
                <>
                {!hasExclusiveRule && (
                  <MenuItem
                    onClick={() => {
                      setDraftValues(allValuesSelected ? [] : [ALL_OPTION_VALUE]);
                      setPendingClearAllFilters(false);
                      setDraftDirty(true);
                    }}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      minHeight: 32,
                      width: '100%',
                      '&:hover': { backgroundColor: `${dropdownColors.optionHoverBackground} !important` },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                      <Checkbox
                        checked={allValuesSelected}
                        disableRipple
                        icon={
                          <UncheckedFilterIcon
                            borderColor="#AAAAAA"
                            backgroundColor={dropdownColors.checkboxBackground}
                          />
                        }
                        checkedIcon={
                          <CheckedFilterIcon
                            borderColor={dropdownColors.checkboxBorder}
                            checkColor={dropdownColors.checkboxCheck}
                            backgroundColor={dropdownColors.checkboxBackground}
                          />
                        }
                        sx={{ p: 0 }}
                      />
                      <Typography sx={{ fontSize: 13, color: dropdownColors.optionText }}>All</Typography>
                    </Stack>
                  </MenuItem>)}
                  {showMiscellaneousOption && (
                    <MenuItem
                      onMouseEnter={(event) => setMiscellaneousAnchor(event.currentTarget)}
                      onMouseLeave={scheduleMiscellaneousClose}
                      onClick={(event) => setMiscellaneousAnchor(event.currentTarget)}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        minHeight: 32,
                        width: '100%',
                        '&:hover': { backgroundColor: `${dropdownColors.optionHoverBackground} !important` },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                        <Checkbox
                          checked={someMiscellaneousSelected}
                          disableRipple
                          onClick={(event) => event.stopPropagation()}
                          onChange={toggleMiscellaneousValues}
                          icon={
                            <UncheckedFilterIcon
                              borderColor="#AAAAAA"
                              backgroundColor={dropdownColors.checkboxBackground}
                            />
                          }
                          checkedIcon={
                            <CheckedFilterIcon
                              borderColor={dropdownColors.checkboxBorder}
                              checkColor={dropdownColors.checkboxCheck}
                              backgroundColor={dropdownColors.checkboxBackground}
                            />
                          }
                          sx={{ p: 0 }}
                        />
                        <Typography sx={{ flex: 1, fontSize: 13, color: dropdownColors.optionText }}>
                          Miscellaneous
                        </Typography>
                        <ArrowRightIcon color={dropdownColors.optionText} />
                      </Stack>
                    </MenuItem>
                  )}
                  {filteredValueOptions.map((option) => {
                  const checked = allValuesSelected || selectedValueSet.has(option.value);
                  const disabled = !checked && isOptionDisabled(option.value);

                  return (
                    <MenuItem
                      key={option.value}
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        const nextValues = new Set(getExpandedDraftValues());
                        if (checked) {
                          nextValues.delete(option.value);
                        } else {
                          nextValues.add(option.value);
                        }

                        updateDraftSelection(nextValues);
                        setPendingClearAllFilters(false);
                        setDraftDirty(true);
                      }}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        minHeight: 32,
                        width: '100%',
                        '&:hover': { backgroundColor: `${dropdownColors.optionHoverBackground} !important` },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          disableRipple
                          icon={
                            <UncheckedFilterIcon
                              borderColor="#AAAAAA"
                              backgroundColor={dropdownColors.checkboxBackground}
                            />
                          }
                          checkedIcon={
                            <CheckedFilterIcon
                              borderColor={dropdownColors.checkboxBorder}
                              checkColor={dropdownColors.checkboxCheck}
                              backgroundColor={dropdownColors.checkboxBackground}
                            />
                          }
                          sx={{
                            p: 0,
                          }}
                        />
                        <Typography sx={{ fontSize: 13, color: dropdownColors.optionText }}>{option.label}</Typography>
                      </Stack>
                    </MenuItem>
                  );
                  })}
                </>}

              {!loadingValues && !hasFilteredValueOptions && (
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>No results</Typography>
                </Box>
              )}
            </MenuList>
          </Box>
        </Box>
      )}

      <Popper
        open={Boolean(miscellaneousAnchor) && showMiscellaneousOption}
        anchorEl={miscellaneousAnchor}
        placement="right-start"
        sx={{ zIndex: 1650 }}
      >
        <Paper
          elevation={4}
          onMouseEnter={clearMiscellaneousCloseTimer}
          onMouseLeave={scheduleMiscellaneousClose}
          sx={{
            width: 220,
            maxHeight: VALUE_LIST_HEIGHT,
            overflowY: 'auto',
            backgroundColor: `${dropdownColors.panelBackground} !important`,
            color: `${dropdownColors.optionText} !important`,
            scrollbarWidth: 'thin',
            scrollbarColor: `${dropdownColors.checkboxCheck} ${dropdownColors.panelBorder}`,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: dropdownColors.panelBorder,
            },
            '&::-webkit-scrollbar-thumb': {
              background: dropdownColors.checkboxCheck,
              borderRadius: '8px',
              border: `2px solid ${dropdownColors.panelBorder}`,
            },
          }}
        >
          <MenuList dense disablePadding sx={{ width: '100%' }}>
            <MenuItem
              onClick={toggleMiscellaneousValues}
              sx={{
                px: 1.5,
                py: 0.75,
                minHeight: 32,
                width: '100%',
                '&:hover': { backgroundColor: `${dropdownColors.optionHoverBackground} !important` },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Checkbox
                  checked={allMiscellaneousSelected}
                  disableRipple
                  onClick={(event) => event.stopPropagation()}
                  onChange={toggleMiscellaneousValues}
                  icon={
                    <UncheckedFilterIcon
                      borderColor="#AAAAAA"
                      backgroundColor={dropdownColors.checkboxBackground}
                    />
                  }
                  checkedIcon={
                    <CheckedFilterIcon
                      borderColor={dropdownColors.checkboxBorder}
                      checkColor={dropdownColors.checkboxCheck}
                      backgroundColor={dropdownColors.checkboxBackground}
                    />
                  }
                  sx={{ p: 0 }}
                />
                <Typography sx={{ fontSize: 13, color: dropdownColors.optionText }}>All</Typography>
              </Stack>
            </MenuItem>
            {visibleMiscellaneousValues.map((value: any) => {
              const valueKey = String(getOptionValue(value));
              const checked = allValuesSelected || selectedValueSet.has(valueKey);
              return (
                <MenuItem
                  key={valueKey}
                  onClick={() => toggleMiscellaneousValue(value)}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    minHeight: 32,
                    width: '100%',
                    '&:hover': { backgroundColor: `${dropdownColors.optionHoverBackground} !important` },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Checkbox
                      checked={checked}
                      disableRipple
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleMiscellaneousValue(value)}
                      icon={
                        <UncheckedFilterIcon
                          borderColor="#AAAAAA"
                          backgroundColor={dropdownColors.checkboxBackground}
                        />
                      }
                      checkedIcon={
                        <CheckedFilterIcon
                          borderColor={dropdownColors.checkboxBorder}
                          checkColor={dropdownColors.checkboxCheck}
                          backgroundColor={dropdownColors.checkboxBackground}
                        />
                      }
                      sx={{ p: 0 }}
                    />
                    <Typography sx={{ fontSize: 13, color: dropdownColors.optionText }}>
                      {getOptionLabel(value, column)}
                    </Typography>
                  </Stack>
                </MenuItem>
              );
            })}
          </MenuList>
        </Paper>
      </Popper>

      <Popper
        open={Boolean(submenuAnchor)}
        anchorEl={submenuAnchor}
        placement="right-start"
        sx={{ zIndex: 1600 }}
      >
        <Paper
          elevation={4}
          onMouseEnter={clearSubmenuCloseTimer}
          onMouseLeave={() => scheduleSubmenuClose()}
          sx={{
            width: 190,
            backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
            color: `${colors.inputNew.inputText} !important`,
          }}
        >
          <MenuList dense disablePadding>
            {operators.map((o) => (
              <MenuItem
                key={o.operator}
                sx={{
                  fontSize: 13,
                  color: `${colors.inputNew.inputText} !important`,
                  '&:hover': {
                    backgroundColor: `${colors.dropdown.checkBox} !important`,
                  },
                }}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  const popup = submenuAnchor?.closest('.MuiPopover-paper');
                  const popupRect = popup?.getBoundingClientRect();
                  const dialogWidth = 680;
                  const dialogHeight = 220;
                  const gap = 8;
                  const rightEdge = window.innerWidth - 16;
                  const bottomEdge = window.innerHeight - 16;
                  const rightPosition = popupRect ? popupRect.right + gap : event.clientX + gap;
                  const leftPosition = popupRect ? popupRect.left - dialogWidth - gap : event.clientX - dialogWidth - gap;
                  const left = rightPosition + dialogWidth <= rightEdge ? rightPosition : leftPosition;
                  const idealTop = popupRect
                    ? popupRect.top + popupRect.height / 2 - dialogHeight / 2
                    : event.clientY - dialogHeight / 2;
                  const top = Math.min(Math.max(16, idealTop), bottomEdge - dialogHeight);

                  setCustomFilterPosition({
                    top: Math.max(16, top),
                    left: Math.max(16, left),
                  });
                  setSubmenuAnchor(null);
                  setCustomFilterOperator(o.operator);
                }}
              >
                {o.label}
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>

      {hasOperators && hasValueList && (
        <Divider
          sx={{
            my: 0.5,
            borderColor: `${colors.inputNew.inputPlaceholder}`,
          }}
        />
      )}
      
      {renderFooterButtons()}

      {customFilterOperator && (
        <CustomFilterDialog
          open
          columnLabel={columnLabel}
          dataType={dataType}
          allowedOperators={column.filterOperators}
          position={customFilterPosition}
          initialOperator={customFilterOperator}
          initialCondition={
            pendingCustomCondition ??
            (currentFilter?.condition?.operator === customFilterOperator
              ? currentFilter.condition
              : null)
          }
          onCancel={closeCustomFilter}
          onApply={handleApplyCustomFilter}
        />
      )}

    </Box>
  );
};

export default ColumnFilterControl;